// Mobile control scheme: 4-button (left/right/go/stop) or twin-stick
// differential (one vertical slider per engine, drag up/down for forward/
// reverse thrust). Persisted across sessions. Body class drives the CSS
// that swaps which control surface is visible.

const STORAGE_KEY = 'tst:control-mode';

export type ControlMode = 'buttons' | 'sticks';

type Listener = (mode: ControlMode) => void;

const listeners = new Set<Listener>();
let mode = readMode();

export const controlMode = {
  get(): ControlMode {
    return mode;
  },
  set(next: ControlMode): void {
    if (next === mode) return;
    mode = next;
    writeMode(next);
    applyControlMode(next);
    for (const l of listeners) l(next);
  },
  toggle(): ControlMode {
    controlMode.set(mode === 'buttons' ? 'sticks' : 'buttons');
    return mode;
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

/** Sync the body class so CSS can swap the control surface. */
export function applyControlMode(value: ControlMode): void {
  if (typeof document === 'undefined') return;
  document.body.classList.toggle('is-controls-sticks', value === 'sticks');
  document.body.classList.toggle('is-controls-buttons', value === 'buttons');
}

function readMode(): ControlMode {
  try {
    const raw = typeof localStorage?.getItem === 'function'
      ? localStorage.getItem(STORAGE_KEY)
      : null;
    return raw === 'sticks' ? 'sticks' : 'buttons';
  } catch {
    return 'buttons';
  }
}

function writeMode(value: ControlMode): void {
  try {
    if (typeof localStorage?.setItem === 'function') {
      localStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // Ignore storage failures in embedded/test environments.
  }
}
